import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  NumberField,
  ShowButton,
  TagField,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IOrder, IVessel } from "interfaces";
import React from "react";
import { getStatusTagColor } from "constants/tagColors";

export const OrderList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IOrder>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: orderStatusSelectProps } = useSelect<IOrder>({
    resource: "order",
    optionLabel: "orderStatus",
    optionValue: "orderStatus",

    pagination: {
      mode: "server"
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IOrder>({
    mapData: (item) => {
      return {
        "IMO Number": item?.licence?.vessel?.imoNumber || "",
        "Vessel Name": item?.licence?.vessel?.name || "",
        "Order Date": item?.orderDate || "",
        "Order Status": item?.orderStatus || "",
        "Currency": item?.currency || "",
        "Total Price": item?.totalPrice || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "IMO Number",
        "Vessel Name",
        "Order Date",
        "Order Status",
        "Currency",
        "Total Price",
      ],
    },
  });

  const columns: ColumnsType<IOrder> = [
    {
      title: "IMO Number",
      dataIndex: ["licence", "vessel", "imoNumber"],
      key: "licenceVessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 250 }}
            mode="multiple"
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["licence", "vessel", "name"],
      key: "licenceVessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 300 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("orderDate", sorters),
      sorter: true,
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      width: 120,
      render: (value) => (
        <TagField color={getStatusTagColor(value)} value={value} />
      ),
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 300 }}
            mode="multiple"
            placeholder="Select Order Status"
            {...orderStatusSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      width: 100,
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("currency", sorters),
      sorter: true,
    },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 120,
      align: "right",
      render: (value) => <NumberField value={value} />,
      defaultSortOrder: getDefaultSortOrder("totalPrice", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active orders",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
